/**
 * P3 — Site: Blocks with equipment drawer (UX-002 §09). Block cards ranked by
 * deviation from the site median; a drawer (one at a time) for the block's
 * devices. Headline tiles, chip filters, three-metric cards with deviation bar
 * and inverter strip, tabbed drawer with inverter table + band chart.
 *
 * "Raise work order" button is NeuraMach Phase 2 (W3) — shown only when the
 * capability exists, as a disabled stub.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { PageHeader } from "../shell/PageHeader";
import { SiteTabBar } from "../components/SiteTabBar";
import {
  useGetV1SitesIdBlocks,
  useGetV1SitesIdNow,
  useGetV1BlocksId,
  useGetV1Series,
  type GetV1SitesIdBlocksParams,
  type EnvelopeBlockListDataItemsItem,
  type EnvelopeBlockListDataItemsItemHealthStrip,
  type EnvelopeBlockDetailDataSeriesRef,
  type EnvelopeBlockDetailDataDevicesItem,
  type InterventionSummaryItem,
  type Figure,
} from "@solarkey/api-client";
import { TimeSeriesTile, BandOutlierChart } from "@solarkey/charts";
import {
  BlockCard,
  HealthBadge,
  InverterStrip,
  Metric,
  StateWord,
  SearchInput,
  Skeleton,
  ErrorState,
  EmptyState,
  type HealthState,
  type InverterCell,
} from "@solarkey/ui";

import { useCanAction, useEntitlement } from "../session/capabilities";

type Period = GetV1SitesIdBlocksParams["period"];

function figureNumber(f?: Figure): number | undefined {
  return typeof f?.value === "number" ? f.value : undefined;
}
/** Display energy in MWh when large — identical rule to Site:Now (wireframe shows MWh). */
function toDisplayEnergy(f?: Figure): Figure | undefined {
  if (!f) return f;
  const v = figureNumber(f);
  if (f.unit === "kWh" && v !== undefined && Math.abs(v) >= 1000) {
    return { ...f, value: v / 1000, unit: "MWh" };
  }
  return f;
}

type HealthFilter = "all" | "fault" | "warning" | "partial";
type RankBy = "deviation" | "power" | "name";

const HEALTH_PRIORITY: Record<HealthState, number> = {
  fault: 0,
  warning: 1,
  offline: 2,
  partial: 3,
  ok: 4,
  unmonitored: 5,
};

/* The health strip carries two states the generated client does not type yet:
   `standby` (reporting on cadence, generating nothing — night or curtailment)
   and `partial` (reporting, but the AC-power signal is absent). They used to be
   folded into `offline`, which made the whole estate read "offline" after
   sunset. */
function stripCount(s: EnvelopeBlockListDataItemsItemHealthStrip | undefined, key: string): number {
  const v = (s as Record<string, unknown> | undefined)?.[key];
  return typeof v === "number" ? v : 0;
}

function stripTotal(s?: EnvelopeBlockListDataItemsItemHealthStrip): number {
  if (!s) return 0;
  return (s.ok ?? 0) + (s.warning ?? 0) + (s.fault ?? 0) + (s.offline ?? 0)
    + (s.unmonitored ?? 0) + stripCount(s, "standby") + stripCount(s, "partial");
}

function blockHealth(strip?: EnvelopeBlockListDataItemsItemHealthStrip): HealthState {
  if (!strip) return "unmonitored";
  if ((strip.fault ?? 0) > 0) return "fault";
  if ((strip.warning ?? 0) > 0) return "warning";
  if ((strip.offline ?? 0) > 0) return "offline";
  if (stripCount(strip, "partial") > 0) return "partial";
  // Standby is not a fault and not offline — it is a healthy, idle inverter.
  if ((strip.ok ?? 0) > 0 || stripCount(strip, "standby") > 0) return "ok";
  return "unmonitored";
}

function matchesSearch(b: EnvelopeBlockListDataItemsItem, q: string): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    (b.label ?? "").toLowerCase().includes(lower) ||
    (b.block_id ?? "").toLowerCase().includes(lower)
  );
}

function matchesFilter(b: EnvelopeBlockListDataItemsItem, f: HealthFilter): boolean {
  if (f === "all") return true;
  const h = blockHealth(b.health_strip);
  if (f === "fault") return h === "fault";
  if (f === "warning") return h === "warning";
  // `partially_monitored_count` is WITHHELD by the panel (signal-level coverage
  // is not computed). The filter uses the strip's real `partial` count instead.
  if (f === "partial") return stripCount(b.health_strip, "partial") > 0;
  return true;
}

/** Rank for "worst first" ordering, with a withheld figure sorted LAST.
 *
 *  `?? 0` ranked an unmeasured block as if it had deviated by exactly nothing —
 *  the best possible reading — so every withheld block landed in the middle of the
 *  deviation order looking on-target (QA, 25 Oct; the ruling: a withheld figure is
 *  never zero). `-Infinity` puts it at the end of a descending sort whatever the
 *  real values are, which is the pattern the fleet grid already uses. */
function rankDesc(value?: number | null, abs = false): number {
  if (value == null) return -Infinity;
  return abs ? Math.abs(value) : value;
}

function sortBlocks(items: EnvelopeBlockListDataItemsItem[], by: RankBy): EnvelopeBlockListDataItemsItem[] {
  const sorted = [...items];
  if (by === "deviation") {
    sorted.sort((a, b) => rankDesc(b.deviation?.value, true) - rankDesc(a.deviation?.value, true));
  } else if (by === "power") {
    sorted.sort((a, b) => rankDesc(b.power?.value) - rankDesc(a.power?.value));
  } else {
    sorted.sort((a, b) => (a.label ?? a.block_id ?? "").localeCompare(b.label ?? b.block_id ?? ""));
  }
  return sorted;
}

function stripToCells(strip?: EnvelopeBlockListDataItemsItemHealthStrip): InverterCell[] {
  if (!strip) return [];
  const cells: InverterCell[] = [];
  const states: Array<[HealthState, number]> = [
    ["fault", strip.fault ?? 0],
    ["warning", strip.warning ?? 0],
    ["offline", strip.offline ?? 0],
    ["partial", stripCount(strip, "partial")],
    ["ok", strip.ok ?? 0],
    // A standby inverter is healthy and idle; it is drawn as ok, not offline.
    ["ok", stripCount(strip, "standby")],
    ["unmonitored", strip.unmonitored ?? 0],
  ];
  for (const [state, count] of states) {
    for (let i = 0; i < count; i++) {
      cells.push({ id: `${state}-${cells.length}`, health: state });
    }
  }
  return cells;
}

/* ── Main screen ───────────────────────────────────────────────────────── */
export function SiteBlocksScreen(props: {
  siteId: string;
  period?: Period;
}): ReactNode {
  const [openBlock, setOpenBlock] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<HealthFilter>("all");
  const [rankBy, setRankBy] = useState<RankBy>("deviation");

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Equipment opens on its own P7 page, never inline in the block drawer.
  const navigate = useNavigate();
  const scopeMatch = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)/);
  const openEquipmentPage = (id: string) => {
    const tenant = scopeMatch?.[1] ?? "";
    const scope = scopeMatch?.[2] ?? "";
    void navigate({
      to: `/t/${tenant}/s/${scope}/plants/${props.siteId}/equipment` as never,
      search: { equipment: id } as never,
    });
  };
  const query = useGetV1SitesIdBlocks(props.siteId, { period: props.period });

  // Canonical site figures (Energy today, Coverage, Active power, Findings) come
  // from the SAME Site:Now panel the Site:Now screen renders — one canonical
  // figure per quantity (wireframe §09 annotation ②).
  const nowQuery = useGetV1SitesIdNow(
    props.siteId,
    { period: props.period } as Parameters<typeof useGetV1SitesIdNow>[1],
  );
  const nowData = nowQuery.data?.status === 200 ? nowQuery.data.data.data : undefined;
  const tiles = nowData?.tiles;
  const attention = nowData?.attention ?? [];

  const allItems = query.data?.data?.data?.items ?? [];

  const visible = useMemo(
    () => sortBlocks(allItems.filter((b) => matchesSearch(b, search) && matchesFilter(b, filter)), rankBy),
    [allItems, search, filter, rankBy],
  );

  if (query.isLoading) {
    return (
      <div style={grid}>
        <Skeleton variant="tile" />
        <Skeleton variant="tile" />
        <Skeleton variant="tile" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load blocks"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const faultCount = allItems.filter((b) => blockHealth(b.health_strip) === "fault").length;
  const warnCount = allItems.filter((b) => blockHealth(b.health_strip) === "warning").length;
  const partialCount = allItems.filter((b) => stripCount(b.health_strip, "partial") > 0).length;
  const totalInverters = allItems.reduce(
    (sum, b) => sum + (b.inverter_count ?? stripTotal(b.health_strip)),
    0,
  );
  const icrCount = new Set(
    allItems.map((b) => b.icr).filter((v): v is string => !!v),
  ).size;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PageHeader title="Blocks" showTimeControl={false} />
      <SiteTabBar site={props.siteId} />

      {/* ── Headline tiles (wireframe §09 row 1): Blocks · Coverage ·
          Active power · Energy today · Findings. Coverage / Active power /
          Energy today are the SAME canonical figures as Site:Now. ───────── */}
      <div className="sk-tiles-5">
        {/* 1 — Blocks */}
        <div className="sk-tile">
          <span className="sk-tile__label">Blocks</span>
          <span className="sk-metric">
            <span className="sk-metric__value">{allItems.length}</span>
          </span>
          <span className="sk-tile__sub">
            {icrCount > 0 ? `${icrCount} ICR · ` : ""}{totalInverters} inverters
          </span>
        </div>

        {/* 2 — Coverage (canonical, Site:Now) */}
        <div className="sk-tile">
          <span className="sk-tile__label">Coverage</span>
          {tiles?.coverage?.signal_level ? (
            <>
              <Metric figure={tiles.coverage.signal_level} showBasis={false} showFreshness={false} showProvenance={false} />
              <span className="sk-tile__sub">
                signal level
                {tiles.coverage.partially_monitored && tiles.coverage.partially_monitored.length > 0
                  ? ` · ${tiles.coverage.partially_monitored.length} partially monitored`
                  : null}
              </span>
            </>
          ) : <StateWord state="NOT_MEASURED" />}
        </div>

        {/* 3 — Active power (canonical, Site:Now) */}
        <div className="sk-tile">
          <span className="sk-tile__label">Active power</span>
          {tiles?.active_power ? (
            <>
              <Metric figure={tiles.active_power} showBasis={false} showFreshness={false} showProvenance={false} />
              {(() => {
                const prov = tiles.active_power?.provenance;
                return prov ? (
                  <span className="sk-tile__sub"><span className="sk-basis-chip">{prov}</span></span>
                ) : null;
              })()}
            </>
          ) : <StateWord state="NOT_MEASURED" />}
        </div>

        {/* 4 — Energy today (canonical, Site:Now — one figure per quantity) */}
        <div className="sk-tile">
          <span className="sk-tile__label">Energy today</span>
          {tiles?.energy_today?.delivered ? (
            <>
              <Metric figure={toDisplayEnergy(tiles.energy_today.delivered)!} showBasis={false} showFreshness={false} showProvenance={false} maximumFractionDigits={1} />
              <span className="sk-tile__sub">
                {tiles.energy_today.delivered.basis ? (
                  <span className="sk-basis-chip">{tiles.energy_today.delivered.basis}</span>
                ) : null}
                {/* UX-REV-001: dropped "same figure as Site: Now" — read minutes
                    apart the two drift with live accrual, so the claim invited a
                    "why don't they match?" question. */}
              </span>
            </>
          ) : <StateWord state="NOT_MEASURED" />}
        </div>

        {/* 5 — Findings (canonical, Site:Now attention list) */}
        <div className="sk-tile">
          <span className="sk-tile__label">Findings</span>
          <span className="sk-metric">
            <span className="sk-metric__value">{attention.length}</span>
            <span className="sk-metric__unit">open</span>
          </span>
          <span className="sk-tile__sub">
            {attention.length > 0 ? (attention[0]?.class ?? "needs attention") : "all clear"}
          </span>
        </div>
      </div>

      {/* ── Filter + rank bar (wireframe §09 row 2) ───────────────── */}
      <div style={toolbar}>
        <SearchInput
          label="Find block or inverter"
          placeholder="Find block, inverter, serial…"
          hideLabel
          value={search}
          onChange={setSearch}
        />
        <div style={chipGroup} role="group" aria-label="Filter by health">
          <FilterChip label="All" active={filter === "all"} onClick={() => setFilter("all")} />
          <FilterChip label={`Faults ${faultCount}`} active={filter === "fault"} onClick={() => setFilter("fault")} />
          <FilterChip label={`Warnings ${warnCount}`} active={filter === "warning"} onClick={() => setFilter("warning")} />
          <FilterChip label={`Partially monitored ${partialCount}`} active={filter === "partial"} onClick={() => setFilter("partial")} />
        </div>
        <span style={rankLabelStyle}>Rank by</span>
        <div style={chipGroup} role="group" aria-label="Rank by">
          <FilterChip label="Deviation" active={rankBy === "deviation"} onClick={() => setRankBy("deviation")} />
          <FilterChip label="Power" active={rankBy === "power"} onClick={() => setRankBy("power")} />
          <FilterChip label="Name" active={rankBy === "name"} onClick={() => setRankBy("name")} />
        </div>
      </div>

      {/* ── Block cards grid (wireframe §09 row 3) ────────────────── */}
      {visible.length === 0 ? (
        <EmptyState
          variant={allItems.length === 0 ? "no_data" : "verified_quiet"}
          title={allItems.length === 0 ? "No blocks mapped" : "No blocks match"}
          message={allItems.length === 0 ? "Devices appear under an unassigned group until block mapping exists." : undefined}
        />
      ) : (
        <div style={blockGrid}>
          {visible.map((b, i) => {
            const invCells = stripToCells(b.health_strip);
            const invCount = b.inverter_count ?? stripTotal(b.health_strip);
            const capMw = b.ac_capacity_kw ? `${Math.round(b.ac_capacity_kw / 1000)} MW · ` : "";
            const subtitle = `${b.icr ? `${b.icr} · ` : ""}${capMw}${invCount} inverters`;
            return (
              <BlockCard
                key={b.block_id ?? i}
                title={b.label ?? b.block_id ?? "Block"}
                subtitle={subtitle}
                health={blockHealth(b.health_strip)}
                selected={openBlock === b.block_id}
                metric={b.power}
                specificYield={b.specific_yield}
                deviationPct={
                  typeof b.deviation?.value === "number" ? b.deviation.value : undefined
                }
                partiallyMonitored={stripCount(b.health_strip, "partial") || undefined}
                partiallyMonitoredLabel="reporting partially"
                inverterStrip={
                  invCells.length > 0 ? <InverterStrip items={invCells} density="compact" /> : undefined
                }
                onOpen={() => {
                  // Toggle: clicking the already-open block closes its drawer
                  // (back to the cards), clicking another switches to it.
                  setOpenBlock((cur) => (cur === b.block_id ? null : (b.block_id ?? null)));
                }}
              />
            );
          })}
        </div>
      )}

      {/* ── Drawer, inline below the cards (one at a time, wireframe §09
          annotation 4; sir's .drawer is in the content flow, not a modal). ── */}
      {openBlock ? (
        (() => {
          const ob = allItems.find((b) => b.block_id === openBlock);
          const obInv = ob?.inverter_count ?? stripTotal(ob?.health_strip);
          const obCap = ob?.ac_capacity_kw ? `${Math.round(ob.ac_capacity_kw / 1000)} MW · ` : "";
          const title = ob?.label ?? ob?.block_id ?? "Block";
          const subtitle = ob
            ? `${ob.icr ? `${ob.icr} · ` : ""}${obCap}${obInv} inverters`
            : undefined;
          return (
            <InlineDrawer title={title} subtitle={subtitle} detailHref={`${pathname}/${openBlock}`} onClose={() => setOpenBlock(null)}>
              <BlockBody
                blockId={openBlock}
                period={props.period}
                onOpenEquipment={(id) => openEquipmentPage(id)}
              />
            </InlineDrawer>
          );
        })()
      ) : null}
    </div>
  );
}

/* ── Inline drawer (wireframe §09 .drawer — in the content flow, below the
   cards, one at a time; Esc closes). ───────────────────────────────────── */
function InlineDrawer(props: {
  title: string;
  subtitle?: string;
  detailHref?: string;
  onClose: () => void;
  children: ReactNode;
}): ReactNode {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props]);
  return (
    <section style={inlineDrawerStyle} role="region" aria-label={props.title}>
      <header style={inlineDrawerHead}>
        <strong style={{ font: "600 14px/1.2 var(--sk-font-text)", color: "var(--sk-color-ink-1)" }}>
          {props.title}
        </strong>
        {props.subtitle ? <span style={muted}>{props.subtitle}</span> : null}
        {props.detailHref ? (
          <Link to={props.detailHref as never} style={drawerDetailLink}>
            Open full detail &#8250;
          </Link>
        ) : null}
        <button type="button" style={inlineDrawerClose} onClick={props.onClose}>
          Close
        </button>
      </header>
      {props.children}
    </section>
  );
}

/* ── Filter chip ───────────────────────────────────────────────────────── */
function FilterChip(props: { label: string; active: boolean; onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      style={props.active ? chipActive : chip}
      aria-pressed={props.active}
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
}

/* ── Block drawer body ──────────────────────────────────────────────────── */
type DrawerTab = "inverters" | "transformer" | "ht_panel" | "findings";

function BlockBody(props: {
  blockId: string;
  period?: Period;
  onOpenEquipment: (equipmentId: string) => void;
}): ReactNode {
  const [activeTab, setActiveTab] = useState<DrawerTab>("inverters");
  const canRaiseWo = useCanAction("raise_work_order");
  const hasExpectedVsActual = useEntitlement("expected_vs_actual");
  const q = useGetV1BlocksId(props.blockId, { period: props.period });

  const ok = q.data?.status === 200 ? q.data : undefined;
  const data = ok?.data.data;
  const devices = data?.devices ?? [];

  // Tab by registry CLASS, not by name: "Inverter Duty Transformer" and
  // "Inverter 1 · SMB-3" both contain "inv" but are a transformer and a DC
  // string-combiner, not inverters (wireframe §09 tabs are per equipment type).
  const inverters = useMemo(
    () => devices.filter((d: EnvelopeBlockDetailDataDevicesItem) => d.device_class === "inverter"),
    [devices],
  );
  const transformers = useMemo(
    () => devices.filter((d: EnvelopeBlockDetailDataDevicesItem) => d.device_class === "transformer"),
    [devices],
  );
  // HT panel = the HT-side switchgear and metering (VCB/breaker, isolator, CT,
  // PT, block MFM) — one tab in the wireframe.
  const HT_CLASSES = new Set(["breaker", "isolator", "ct", "pt", "meter", "ht_panel", "panel"]);
  const htPanels = useMemo(
    () => devices.filter((d: EnvelopeBlockDetailDataDevicesItem) => HT_CLASSES.has(d.device_class ?? "")),
    [devices], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (q.isLoading) return <Skeleton variant="text" lines={4} />;
  if (q.isError || !ok) {
    return <ErrorState title="Couldn't load block" />;
  }

  const findings = data?.findings ?? [];
  const seriesRef = data?.series_ref;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* ── Tabs (wireframe §09: Inverters/Transformer/HT panel/Findings) ── */}
      <div style={tabBar} role="tablist">
        <TabButton label={`Inverters (${inverters.length})`} active={activeTab === "inverters"} onClick={() => setActiveTab("inverters")} />
        <TabButton label={`Transformer (${transformers.length})`} active={activeTab === "transformer"} onClick={() => setActiveTab("transformer")} />
        <TabButton label={`HT panel (${htPanels.length})`} active={activeTab === "ht_panel"} onClick={() => setActiveTab("ht_panel")} />
        <TabButton label={`Findings (${findings.length})`} active={activeTab === "findings"} onClick={() => setActiveTab("findings")} />
      </div>

      {/* ── Tab content ──────────────────────────────────────────── */}
      {activeTab === "inverters" ? (
        <div style={twoCol}>
          <DeviceTable devices={inverters} label="inverter" collapse onOpen={(id) => props.onOpenEquipment(id)} />
          <div>
            <BlockSeries seriesRef={seriesRef} showBand={hasExpectedVsActual} />
            {findings.length > 0 && findings[0] ? (
              <FindingPinned finding={findings[0]} canRaiseWo={canRaiseWo} />
            ) : null}
          </div>
        </div>
      ) : activeTab === "transformer" ? (
        <DeviceTable devices={transformers} label="transformer" onOpen={(id) => props.onOpenEquipment(id)} />
      ) : activeTab === "ht_panel" ? (
        <DeviceTable devices={htPanels} label="device" onOpen={(id) => props.onOpenEquipment(id)} />
      ) : (
        <FindingsList count={findings.length} items={findings} canRaiseWo={canRaiseWo} />
      )}
    </div>
  );
}

/* ── Tab button ─────────────────────────────────────────────────────── */
function TabButton(props: { label: string; active: boolean; onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={props.active}
      style={props.active ? tabActive : tabStyle}
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
}

/* ── Device table (wireframe §09 left column) ──────────────────────────────
   Used for all equipment tabs. For inverters (collapse) it folds the within-band
   healthy ones into a summary row, keeping escapees visible; for a handful of
   transformers / HT-panel devices it shows every row. */
const _INV_CAP = 12;
function DeviceTable(props: {
  devices: EnvelopeBlockDetailDataDevicesItem[];
  onOpen: (id: string) => void;
  label?: string;
  collapse?: boolean;
}): ReactNode {
  const sorted = useMemo(() => {
    const items = [...props.devices];
    items.sort((a, b) => {
      const pa = HEALTH_PRIORITY[(a.health?.state ?? "unmonitored") as HealthState] ?? 5;
      const pb = HEALTH_PRIORITY[(b.health?.state ?? "unmonitored") as HealthState] ?? 5;
      return pa - pb;
    });
    return items;
  }, [props.devices]);

  const escapees = sorted.filter(
    (d) => Math.abs(d.deviation?.value ?? 0) > 5 || (d.health?.state ?? "ok") !== "ok",
  );
  // Only collapse (fold healthy ones into "N more within band") for inverters,
  // and only when the list is long. Otherwise show every row — a block with 5
  // inverters must list all 5.
  const shown = !props.collapse
    ? sorted
    : escapees.length > 0
      ? escapees
      : sorted.slice(0, _INV_CAP);
  const hidden = sorted.length - shown.length;
  const noun = props.label ?? "device";

  // One selected row at a time, highlighted (wireframe §09: the focused row gets
  // the light-blue band + a primary Open). Default to the first row shown.
  const [selected, setSelected] = useState<string | null>(null);
  const selectedId = selected ?? shown[0]?.device_id ?? null;

  if (sorted.length === 0) {
    return <EmptyState variant="no_data" title="No devices in this category" />;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>{noun.charAt(0).toUpperCase() + noun.slice(1)}</th>
            <th style={th}>Health</th>
            <th style={thRight}>Power</th>
            <th style={thRight}>Today</th>
            <th style={thRight}>vs block</th>
            <th style={th}>Fresh</th>
            <th style={th}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {shown.map((d, i) => {
            const isSel = !!d.device_id && d.device_id === selectedId;
            return (
              <tr
                key={d.device_id ?? i}
                onClick={() => d.device_id && setSelected(d.device_id)}
                style={{ cursor: "pointer", ...(isSel ? trSelected : d.health?.state === "fault" ? trFault : undefined) }}
              >
                <td style={td}>
                  <strong>{d.role_name ?? d.device_id}</strong>
                </td>
                <td style={td}>
                  <HealthBadge state={(d.health?.state ?? "unmonitored") as HealthState} />
                </td>
                <td style={tdRight}>
                  {d.power ? <Metric className="sk-metric--inv" figure={d.power} showFreshness={false} showBasis={false} showProvenance={false} /> : "—"}
                </td>
                <td style={tdRight}>
                  {typeof d.energy?.value === "number"
                    ? <Metric className="sk-metric--inv" figure={d.energy} showFreshness={false} showBasis={false} showProvenance={false} />
                    : <span style={{ color: "var(--sk-color-ink-3)" }}>—</span>}
                </td>
                <td style={tdRight}>
                  {typeof d.deviation?.value === "number" ? (
                    <span>
                      {d.deviation.value > 0 ? "+" : ""}
                      {d.deviation.value.toFixed(1)}%
                    </span>
                  ) : "—"}
                </td>
                <td style={td}>
                  {d.freshness?.age_s != null ? formatAge(d.freshness.age_s) : "—"}
                </td>
                <td style={td}>
                  <button
                    type="button"
                    style={isSel ? openBtnPrimary : openBtn}
                    onClick={(e) => { e.stopPropagation(); if (d.device_id) { setSelected(d.device_id); props.onOpen(d.device_id); } }}
                  >
                    Open
                  </button>
                </td>
              </tr>
            );
          })}
          {hidden > 0 ? (
            <tr>
              <td colSpan={7} style={{ ...td, color: "var(--sk-color-ink-3)" }}>
                {hidden} more {noun}{hidden !== 1 ? "s" : ""} within band
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  return `${Math.floor(seconds / 3600)} h`;
}

/* ── Block delivered-series chart ──────────────────────────────────── */
function BlockSeries(props: { seriesRef?: EnvelopeBlockDetailDataSeriesRef; showBand?: boolean }): ReactNode {
  const ref = props.seriesRef;
  const query = useGetV1Series(
    {
      site: ref?.site ?? "",
      signal: ref?.signal ?? "",
      // Scope the curve to THIS block's inverters, not the whole site.
      ...(ref?.block ? { block: ref.block } : {}),
      from: ref?.from ?? "",
      to: ref?.to ?? "",
    },
    { query: { enabled: !!(ref?.site && ref?.signal && ref?.from && ref?.to) } },
  );

  if (!ref?.site || !ref?.signal) {
    return <EmptyState variant="no_data" title="No series for this block" />;
  }
  if (query.isLoading) return <Skeleton variant="tile" />;
  if (query.isError || !query.data || query.data.status !== 200) {
    return <EmptyState variant="no_data" title="AC power" message="Series unavailable" />;
  }

  const s = query.data.data.data;
  const points = s.points ?? [];
  if (points.length === 0) return <EmptyState variant="no_data" title="AC power" />;

  const times = points.map((p) => Date.parse(p.ts) / 1000);
  const values = points.map((p) => p.value ?? null);

  // Clamp the x view to generation hours 06:00–19:00 of the data's day (UX-006 §2).
  const day = times.length > 0 ? new Date(times[0]! * 1000) : new Date();
  const d6 = new Date(day); d6.setHours(6, 0, 0, 0);
  const d19 = new Date(day); d19.setHours(19, 0, 0, 0);
  const genHours: [number, number] = [d6.getTime() / 1000, d19.getTime() / 1000];
  // Same as the block page: /v1/series serves no lo/hi, so the band is ABSENT, not 0.
  const band = points.map((p) => {
    const raw = p as unknown as { lo?: number | null; hi?: number | null };
    return { lo: typeof raw.lo === "number" ? raw.lo : null,
             hi: typeof raw.hi === "number" ? raw.hi : null };
  });
  const hasBand = band.some((b) => b.lo !== null || b.hi !== null);
  const lo = band.map((b) => b.lo ?? 0);
  const hi = band.map((b) => b.hi ?? 0);

  if (hasBand && props.showBand !== false) {
    return (
      <div>
        <div style={chartHeading}>AC power · normalised band</div>
        <BandOutlierChart label="AC power · normalised band" unit={s.unit ?? ""} times={times} values={values} lo={lo} hi={hi} />
      </div>
    );
  }
  return (
    <div>
      <div style={chartHeading}>AC power</div>
      <TimeSeriesTile label="Delivered" unit={s.unit ?? ""} times={times} values={values} xRange={genHours} height={300} />
    </div>
  );
}

/* ── Finding pinned callout (wireframe §09 annotation 6) ─────────── */
function FindingPinned(props: {
  finding: InterventionSummaryItem;
  canRaiseWo?: boolean;
}): ReactNode {
  const f = props.finding;
  return (
    <div style={findingPinnedStyle}>
      <span>
        <strong>{f.class ?? "Finding"}</strong>
        {f.lane ? ` · ${f.lane}` : ""}
      </span>
      {props.canRaiseWo ? (
        <button type="button" style={raiseWoSmall} disabled>
          Raise work order
        </button>
      ) : null}
    </div>
  );
}

/* ── Findings list tab ─────────────────────────────────────────────── */
function FindingsList(props: {
  count: number;
  items: InterventionSummaryItem[];
  canRaiseWo?: boolean;
}): ReactNode {
  return (
    <div>
      {props.count === 0 ? (
        // Not verified quiet: no findings computation runs for a block, so an
        // empty list is an uncomputed figure, not a clean bill of health.
        <EmptyState
          variant="no_data"
          title="Findings are not computed for a block"
          message="No findings store is wired to the block panel, so the count is withheld rather than shown as zero."
        />
      ) : (
        <ul style={list}>
          {props.items.map((f, i) => (
            <li key={f.id ?? i} style={{ ...deviceRow, padding: "6px 0" }}>
              <span className="sk-chip">{f.class ?? "finding"}</span>
              <span style={muted}>{f.lane ?? ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Styles ────────────────────────────────────────────────────────────── */
const blockGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: 12,
};
const grid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  gap: 16,
};
const muted: React.CSSProperties = {
  font: "400 12px/1.5 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
};
const list: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };
const deviceRow: React.CSSProperties = {
  borderBottom: "1px solid var(--sk-color-border-1)",
};
const toolbar: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
};
const inlineDrawerStyle: React.CSSProperties = {
  border: "1px solid var(--sk-color-border-2, var(--sk-color-border-1))",
  borderRadius: "var(--sk-radius-card, 8px)",
  background: "var(--sk-color-surface-0, var(--sk-surface, #fff))",
  padding: 14,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const inlineDrawerHead: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
};
const drawerDetailLink: React.CSSProperties = {
  fontSize: 12,
  color: "var(--sk-color-accent, #0066cc)",
  textDecoration: "none",
  whiteSpace: "nowrap",
};
const inlineDrawerClose: React.CSSProperties = {
  marginLeft: "auto",
  padding: "4px 12px",
  borderRadius: "var(--sk-radius-control, 6px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "var(--sk-surface, #fff)",
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  font: "500 12px/1.35 var(--sk-font-text)",
};
const chipGroup: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
};
const chip: React.CSSProperties = {
  padding: "4px 10px",
  borderRadius: "var(--sk-radius-chip, 4px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "var(--sk-surface, #fff)",
  color: "var(--sk-color-ink-2)",
  cursor: "pointer",
  font: "400 12px/1.35 var(--sk-font-text)",
};
const chipActive: React.CSSProperties = {
  ...chip,
  borderColor: "var(--sk-color-accent)",
  color: "var(--sk-color-accent)",
  fontWeight: 600,
};
const rankLabelStyle: React.CSSProperties = {
  font: "400 12px/1.35 var(--sk-font-text)",
  color: "var(--sk-color-ink-3)",
  marginLeft: "auto",
};

const tabBar: React.CSSProperties = {
  display: "flex",
  gap: 0,
  borderBottom: "2px solid var(--sk-color-border-1)",
};
const tabStyle: React.CSSProperties = {
  padding: "8px 14px",
  background: "transparent",
  border: 0,
  borderBottom: "2px solid transparent",
  marginBottom: -2,
  color: "var(--sk-color-ink-3)",
  cursor: "pointer",
  font: "400 12px/1.35 var(--sk-font-text)",
};
const tabActive: React.CSSProperties = {
  ...tabStyle,
  borderBottomColor: "var(--sk-color-accent)",
  color: "var(--sk-color-ink-1)",
  fontWeight: 600,
};

const twoCol: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.2fr 1fr",
  gap: 16,
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  font: "400 12px/1.5 var(--sk-font-text)",
};
const th: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "2px solid var(--sk-color-border-1)",
  font: "600 10px/1.3 var(--sk-font-text)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--sk-color-ink-3)",
  textAlign: "left",
  whiteSpace: "nowrap",
};
const thRight: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "1px solid var(--sk-color-border-1)",
  verticalAlign: "middle",
};
const tdRight: React.CSSProperties = { ...td, textAlign: "right" };
const trFault: React.CSSProperties = {
  background: "color-mix(in srgb, var(--sk-color-fault, #ef4444) 6%, transparent)",
};
const trSelected: React.CSSProperties = {
  background: "var(--sk-color-accent-soft, color-mix(in srgb, var(--sk-color-accent, #2563eb) 10%, transparent))",
};
const openBtn: React.CSSProperties = {
  padding: "3px 10px",
  borderRadius: "var(--sk-radius-control, 4px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "var(--sk-surface, #fff)",
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  font: "400 11px/1.35 var(--sk-font-text)",
};
const openBtnPrimary: React.CSSProperties = {
  ...openBtn,
  border: 0,
  background: "var(--sk-color-accent)",
  color: "#fff",
  fontWeight: 600,
};
const chartHeading: React.CSSProperties = {
  margin: "0 0 6px",
  font: "600 12px/1.35 var(--sk-font-text)",
  color: "var(--sk-color-ink-2)",
};
const findingPinnedStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  borderRadius: "var(--sk-radius-card, 6px)",
  border: "1px solid var(--sk-color-border-1)",
  background: "color-mix(in srgb, var(--sk-color-warning, #f59e0b) 6%, transparent)",
  font: "400 12px/1.5 var(--sk-font-text)",
  color: "var(--sk-color-ink-1)",
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};
const raiseWoSmall: React.CSSProperties = {
  padding: "3px 10px",
  borderRadius: "var(--sk-radius-control, 4px)",
  border: 0,
  background: "var(--sk-color-accent)",
  color: "#fff",
  cursor: "not-allowed",
  opacity: 0.6,
  font: "600 11px/1.35 var(--sk-font-text)",
  marginLeft: "auto",
};
